import { SceneDataConfig, EntityState } from "../../../config/types";
import { SceneAudioConfig } from "../../../config/audio/types";
import { STATE_TYPE, STATES } from "../../../config/states/entities";
import { stateMappingPlaylistGet } from "../../../config/states/stateMappings";

export type GlobalEntityState = Record<string, string>;

/**
 * Convert scene entity configurations to initial runtime state
 */
export function globalStateFromConfig(entities: readonly EntityState[]): GlobalEntityState {
	const initialState: GlobalEntityState = {};

	entities.forEach((entity) => {
		initialState[entity.id] = entity.value;
	});

	return initialState;
}

/**
 * Compare two scene states to determine if they are equal
 */
export function globalStateIsEqual(state1: GlobalEntityState, state2: GlobalEntityState): boolean {
	const keys1 = Object.keys(state1);
	const keys2 = Object.keys(state2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	// TODO: only will work if there are no nested scene state objects
	return keys1.every((key) => state2.hasOwnProperty(key) && state1[key] === state2[key]);
}

const globalStateBackgroundApply = (
	originalSrc: string,
	state: GlobalEntityState,
	sceneStateEntities: readonly EntityState[],
) => {
	const keys = sceneStateEntities.map((entityName) => entityName.id);
	const stateList = keys.map((k) => state[k]);
	return globalStateUrlApply(stateList, originalSrc);
};

const globalStateAudioApply = (
	audio: SceneAudioConfig,
	state: GlobalEntityState,
	sceneStateEntities: readonly EntityState[],
) => {
	let updatedAudio = audio;

	for (const entity of sceneStateEntities) {
		const currentEntityState = {
			...entity,
			value: state[entity.id],
		} as EntityState;
		updatedAudio = applyEntityStateToAudio(currentEntityState, updatedAudio);
	}
	return updatedAudio;
};
/**
 * Apply state transformations to scene configuration
 * returns new scene configuration
 */
export function globalStateApply(
	config: SceneDataConfig,
	state: GlobalEntityState,
	applyBackground = globalStateBackgroundApply,
	applyAudio = globalStateAudioApply,
): SceneDataConfig {
	const newBackgroundSrc = applyBackground(config.background.src, state, config.state.video);
	const newAudio = applyAudio(config.audio, state, config.state.audio);

	return {
		...config,
		audio: newAudio,
		background: {
			...config.background,
			src: newBackgroundSrc,
		},
	};
}

/**
 * Applies a scene's global state modifiers to a url
 * returns a new encoded url
 */
export function globalStateUrlApply(states: string[], url: string) {
	const DEFAULT = "default";

	// sorting step is very important, must be consistent with file naming
	const sortedStates = states.sort();

	const stateString = sortedStates.join("-");
	const pathParts = url.split("/");
	const sceneDir = pathParts.slice(0, -1).join("/");
	const originalFilename = pathParts.at(-1);
	const suffix = originalFilename?.split("_").slice(1).join("_");
	return `${sceneDir}/${stateString ? stateString : DEFAULT}_${suffix}`;
}

const fireplaceStateApply = (state: string, audio: any) => {
	if (state === STATES.FIREPLACE.OFF) {
		const playlistToRemove = stateMappingPlaylistGet(STATE_TYPE.FIREPLACE);
		const originalPlaylists = audio.playlists || [];
		const filteredPlaylists = originalPlaylists.filter((p: any) => p.name !== playlistToRemove);
		return {
			...audio,
			playlists: filteredPlaylists,
		};
	}
	return audio;
};

const weatherStateApply = (state: string, audio: any) => {
	if (state === STATES.WEATHER.DRIZZLE) {
	}
	return audio;
};

/**
 * applies a single entity state to audio configuration
 */
export function applyEntityStateToAudio(state: EntityState, audio: any) {
	switch (state.type) {
		case "FIREPLACE": {
			return fireplaceStateApply(state.value, audio);
		}
		case "WEATHER": {
			return weatherStateApply(state.value, audio);
		}
		default: {
			return audio;
		}
	}
}
